/**
 * Life Timeline — horizontal scrollable SVG.
 * Top: user-annotated stage bands (from annotations store, consecutive same-stage grouped).
 * Middle: wave/bell curve (ehtiyojlar tsikli) + ruler with all year ticks.
 * Bottom: user custom event emojis (1-3 per year stacked) + turnifly achievements.
 */

import { memo, useRef, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { calcAge } from '@turnifly/shared/utils'
import {
  useLifeAnnotationsStore,
  getPresetById,
  type YearAnnotation,
} from '@turnifly/shared/stores'
import type { TimelineEvent } from '@turnifly/shared/api'

interface LifeTimelineProps {
  birthDate: string
  lifeExpectancy: number
  userAchievements: TimelineEvent[]
}

const PX_PER_YEAR = 24
const PAD = 32
const STAGE_Y = 4
const STAGE_H = 22
const CURVE_TOP = 34
const CURVE_BOTTOM = 108
const RULER_Y = 112
const EVENT_START_Y = 128
const EVENT_LINE_H = 16

export const LifeTimeline = memo(function LifeTimeline({
  birthDate,
  lifeExpectancy,
  userAchievements,
}: LifeTimelineProps) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const age = calcAge(birthDate)
  const birthYear = new Date(birthDate).getFullYear()
  const { annotations } = useLifeAnnotationsStore()

  const totalWidth = PAD * 2 + lifeExpectancy * PX_PER_YEAR
  const ageToX = (a: number): number => PAD + (a / lifeExpectancy) * (totalWidth - PAD * 2)
  const todayX = ageToX(age)

  // Max event stack depth for svg height
  const maxEventDepth = useMemo(() => {
    let max = 0
    for (let yr = birthYear + 1; yr <= birthYear + lifeExpectancy; yr++) {
      const ann = annotations[yr] as YearAnnotation | undefined
      const customCount = ann?.events?.length ?? 0
      const autoCount = userAchievements.filter((a) => new Date(a.date).getFullYear() === yr).length
      max = Math.max(max, customCount + Math.min(autoCount, 3 - customCount))
    }
    return Math.max(max, 1)
  }, [annotations, userAchievements, birthYear, lifeExpectancy])

  const svgHeight = EVENT_START_Y + maxEventDepth * EVENT_LINE_H + 20

  // Auto-scroll to today
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: Math.max(0, todayX - scrollRef.current.clientWidth / 2),
        behavior: 'smooth',
      })
    }
  }, [todayX])

  // ── Group consecutive years with same stage ──
  const stageBands = useMemo(() => {
    const bands: Array<{ stageId: string; color: string; icon: string; label: string; ageStart: number; ageEnd: number }> = []
    let current: typeof bands[0] | null = null

    for (let i = 1; i <= lifeExpectancy; i++) {
      const calYear = birthYear + i
      const ann = annotations[calYear] as YearAnnotation | undefined
      const stageId = ann?.stageId ?? null

      if (stageId && current && current.stageId === stageId) {
        current.ageEnd = i + 1
      } else if (stageId) {
        if (current) bands.push(current)
        const preset = getPresetById(stageId)
        current = {
          stageId,
          color: preset?.color ?? '#64748B',
          icon: preset?.icon ?? '📌',
          label: t(`life.stages.${stageId}`),
          ageStart: i,
          ageEnd: i + 1,
        }
      } else {
        if (current) { bands.push(current); current = null }
      }
    }
    if (current) bands.push(current)
    return bands
  }, [annotations, birthYear, lifeExpectancy, t])

  // ── Wave curve path ──
  const curvePath = useMemo(() => {
    const pts: string[] = []
    for (let a = 0; a <= lifeExpectancy; a++) {
      const x = ageToX(a)
      const peak = lifeExpectancy * 0.45
      const spread = lifeExpectancy * 0.35
      const y = CURVE_BOTTOM - (CURVE_BOTTOM - CURVE_TOP) * Math.exp(-((a - peak) ** 2) / (2 * spread ** 2))
      pts.push(`${a === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`)
    }
    return pts.join(' ')
  }, [lifeExpectancy])

  const fillPath = `${curvePath} L${ageToX(lifeExpectancy).toFixed(1)} ${CURVE_BOTTOM} L${ageToX(0).toFixed(1)} ${CURVE_BOTTOM} Z`

  // ── Collect events per year (custom first, then auto, max 3 total) ──
  const eventsPerYear = useMemo(() => {
    const map = new Map<number, Array<{ emoji: string; label: string }>>()
    for (let yr = birthYear + 1; yr <= birthYear + lifeExpectancy; yr++) {
      const items: Array<{ emoji: string; label: string }> = []
      const ann = annotations[yr] as YearAnnotation | undefined
      if (ann?.events) {
        for (const ev of ann.events) {
          items.push({ emoji: ev.emoji, label: ev.name })
        }
      }
      // Auto events
      const auto = userAchievements.filter((a) => new Date(a.date).getFullYear() === yr)
      for (const a of auto) {
        if (items.length >= 3) break
        items.push({ emoji: a.icon, label: a.label })
      }
      if (items.length > 0) map.set(yr - birthYear, items) // key = age
    }
    return map
  }, [annotations, userAchievements, birthYear, lifeExpectancy])

  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.03] overflow-hidden">
      <div className="px-4 pt-4 pb-2">
        <h3 className="text-sm font-display font-bold text-text">{t('life.lifeTimeline')}</h3>
      </div>

      <div ref={scrollRef} className="overflow-x-auto scrollbar-hide">
        <svg width={totalWidth} height={svgHeight} className="select-none">
          <defs>
            <linearGradient id="curveFill2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0061FF" stopOpacity="0.12" />
              <stop offset="100%" stopColor="#0061FF" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="livedGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#52DAFF" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0061FF" stopOpacity="0.8" />
            </linearGradient>
            <radialGradient id="tGlow">
              <stop offset="0%" stopColor="#52DAFF" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#52DAFF" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* ═══ STAGE BANDS (top) ═══ */}
          {stageBands.map((band, i) => {
            const x1 = ageToX(band.ageStart)
            const x2 = ageToX(band.ageEnd)
            const w = x2 - x1
            return (
              <g key={`band-${i}`}>
                <rect x={x1} y={STAGE_Y} width={w} height={STAGE_H} rx={4}
                  fill={band.color} fillOpacity={0.18} />
                {w > 36 && (
                  <text x={x1 + w / 2} y={STAGE_Y + STAGE_H / 2 + 1}
                    textAnchor="middle" dominantBaseline="central"
                    fill={band.color} fontSize={8} fontWeight={600} opacity={0.9}>
                    {band.icon} {w > 70 ? band.label : ''}
                  </text>
                )}
              </g>
            )
          })}

          {/* ═══ WAVE CURVE ═══ */}
          <path d={fillPath} fill="url(#curveFill2)" />
          <motion.path
            d={curvePath} fill="none"
            stroke="#0061FF" strokeWidth={1.2} strokeOpacity={0.35}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
          />

          {/* ═══ RULER LINE ═══ */}
          <line x1={PAD} y1={RULER_Y} x2={totalWidth - PAD} y2={RULER_Y}
            stroke="rgba(255,255,255,0.06)" strokeWidth={1.5} />

          {/* Lived portion */}
          <motion.line
            x1={PAD} y1={RULER_Y} x2={todayX} y2={RULER_Y}
            stroke="url(#livedGrad)" strokeWidth={2.5} strokeLinecap="round"
            initial={{ x2: PAD }}
            animate={{ x2: todayX }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />

          {/* ═══ ALL YEAR TICKS ═══ */}
          {Array.from({ length: lifeExpectancy + 1 }, (_, i) => i).map((yr) => {
            const x = ageToX(yr)
            const isDecade = yr % 10 === 0
            const isFive = yr % 5 === 0 && !isDecade
            const h = isDecade ? 7 : isFive ? 4 : 2
            return (
              <g key={`t-${yr}`}>
                <line x1={x} y1={RULER_Y - h} x2={x} y2={RULER_Y + h}
                  stroke={isDecade ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)'}
                  strokeWidth={isDecade ? 1 : 0.5} />
                {isDecade && (
                  <text x={x} y={RULER_Y + h + 10} textAnchor="middle"
                    fill="rgba(255,255,255,0.35)" fontSize={9} fontWeight={600}>
                    {yr}
                  </text>
                )}
              </g>
            )
          })}

          {/* ═══ EVENT EMOJIS (below ruler) ═══ */}
          {Array.from(eventsPerYear.entries()).map(([evAge, items]) => {
            const x = ageToX(evAge)
            return (
              <g key={`ev-${evAge}`}>
                {/* Connector line */}
                <line x1={x} y1={RULER_Y + 3} x2={x} y2={EVENT_START_Y - 4}
                  stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} />
                {/* Stacked emojis */}
                {items.map((item, i) => (
                  <text key={i} x={x} y={EVENT_START_Y + i * EVENT_LINE_H}
                    textAnchor="middle" fontSize={11}>
                    {item.emoji}
                  </text>
                ))}
              </g>
            )
          })}

          {/* ═══ TODAY MARKER ═══ */}
          <line x1={todayX} y1={STAGE_Y} x2={todayX} y2={svgHeight - 6}
            stroke="rgba(82,218,255,0.2)" strokeWidth={1} strokeDasharray="3 3" />
          <circle cx={todayX} cy={RULER_Y} r={12} fill="url(#tGlow)" />
          <motion.circle
            cx={todayX} cy={RULER_Y} r={4} fill="#52DAFF"
            animate={{ r: [4, 5.5, 4] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <text x={todayX} y={RULER_Y + 24} textAnchor="middle"
            fill="#52DAFF" fontSize={9} fontWeight={700}>
            {age}
          </text>
        </svg>
      </div>
    </div>
  )
})
